import { Injectable, APP_INITIALIZER, Inject, DOCUMENT } from '@angular/core';
import {
  BootstrapperBase,
  CultureInfoService,
  TOKENS as TOKENS_COMMON,
} from '@trustedshops/tswp-core-common';

@Injectable()
export class UserCentricsBootstrapper implements BootstrapperBase {
  // Give up after ~10 s: an ad blocker can remove the CMP, and an unbounded
  // poll would spin at 20 Hz for the whole session.
  private static readonly CMP_POLL_INTERVAL_MS = 50;
  private static readonly CMP_POLL_MAX_ATTEMPTS = 200;

  //#region Ctor
  public constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    @Inject(TOKENS_COMMON.CultureInfoService)
    private readonly _cultureInfoService: CultureInfoService
  ) {}
  //#endregion

  //#region Public Methods
  public async initialize(): Promise<void> {
    this.initializeUserCentricsLanguageSync();
  }
  //#endregion

  //#region Private Methods
  private async initializeUserCentricsLanguageSync(): Promise<void> {
    let attempts = 0;
    while (!this.userCentricsLoaded()) {
      if (attempts >= UserCentricsBootstrapper.CMP_POLL_MAX_ATTEMPTS) {
        // The CMP never loaded; a language sync is not possible.
        return;
      }
      attempts += 1;
      await new Promise((resolve) =>
        setTimeout(resolve, UserCentricsBootstrapper.CMP_POLL_INTERVAL_MS)
      );
    }

    // Fallback for Usercentrics versions without getActiveLanguage(): when the
    // API is present, the CMP is the source of truth for the active language.
    let lastAppliedLanguage: string | null = null;

    this._cultureInfoService.currentCulture.subscribe((x) => {
      if (!x) {
        return;
      }

      const window: any = this._document.defaultView.window;
      if (!window.UC_UI) {
        return;
      }

      // updateLanguage makes Usercentrics fetch its settings and translation
      // files again. Skip the call when Usercentrics already runs the target
      // language, and skip repeated emissions of the same culture.
      const targetLanguage = x.iso2LanguageCode;
      const activeLanguage =
        window.UC_UI.getActiveLanguage?.() ?? lastAppliedLanguage;

      if (activeLanguage === targetLanguage) {
        lastAppliedLanguage = targetLanguage;
        return;
      }

      window.UC_UI.updateLanguage(targetLanguage);
      lastAppliedLanguage = targetLanguage;
    });
  }

  private userCentricsLoaded(): boolean {
    const window: any = this._document.defaultView.window;
    if (!window.UC_UI) {
      return false;
    }

    return true;
  }
  //#endregion
}

export const UserCentricsInitializer = [
  UserCentricsBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: BootstrapperBase) => {
      return () => initializer.initialize();
    },
    deps: [UserCentricsBootstrapper],
  },
];
